import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import type { OnboardingStepDefinition } from '../../model/types';

interface OnboardingStepperProps {
  steps: OnboardingStepDefinition[];
  currentStep: number;
  completedSteps: number[];
  onStepSelect: (step: OnboardingStepDefinition['step']) => void;
}

const OnboardingStepper: React.FC<OnboardingStepperProps> = ({
  steps,
  currentStep,
  completedSteps,
  onStepSelect,
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('he') || i18n.language.startsWith('ar');

  const prevCompletedRef = useRef<number[]>(completedSteps);
  const [recentlyCompleted, setRecentlyCompleted] = useState<number[]>([]);

  useEffect(() => {
    const newlyCompleted = completedSteps.filter((s) => !prevCompletedRef.current.includes(s));
    if (newlyCompleted.length > 0) {
      setRecentlyCompleted((prev) => [...prev, ...newlyCompleted]);
      const timer = setTimeout(() => {
        setRecentlyCompleted((prev) => prev.filter((s) => !newlyCompleted.includes(s)));
      }, 2000);
      prevCompletedRef.current = completedSteps;
      return () => clearTimeout(timer);
    }
    prevCompletedRef.current = completedSteps;
  }, [completedSteps]);

  const completionRatio = steps.length > 0 ? completedSteps.length / steps.length : 0;

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="grid gap-3 md:grid-cols-5">
        {steps.map((step, index) => {
          const isCurrent = step.step === currentStep;
          const isCompleted = completedSteps.includes(step.step);
          const isInteractive = isCompleted || isCurrent;
          const isJustCompleted = recentlyCompleted.includes(step.step);

          return (
            <motion.div
              key={step.step}
              className="relative"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.07, duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Floating UNLOCKED badge */}
              <AnimatePresence>
                {isJustCompleted && (
                  <motion.div
                    className="pointer-events-none absolute left-1/2 top-0 z-20 -translate-x-1/2 whitespace-nowrap rounded-full bg-emerald-500 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-white shadow-lg shadow-emerald-500/40"
                    initial={{ opacity: 0, y: 0, scale: 0.65 }}
                    animate={{
                      opacity: [0, 1, 1, 1, 0],
                      y: [0, -10, -22, -34, -46],
                      scale: [0.65, 1.05, 1, 1, 0.9],
                    }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
                  >
                    ✓ UNLOCKED
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.button
                type="button"
                disabled={!isInteractive}
                onClick={() => onStepSelect(step.step)}
                whileTap={isInteractive ? { scale: 0.96, transition: { duration: 0.1 } } : {}}
                className={`relative w-full overflow-hidden rounded-2xl border px-4 py-3 transition-colors duration-200 ${isRTL ? 'text-right' : 'text-left'} ${
                  isCurrent
                    ? 'border-brand-500 bg-brand-500 text-white shadow-lg shadow-brand-500/20'
                    : isCompleted
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-900 hover:border-emerald-300 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100'
                      : 'border-neutral-200 bg-white text-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-500'
                } ${isInteractive ? 'cursor-pointer' : 'cursor-not-allowed opacity-80'}`}
              >
                {/* Watermark step number */}
                <AnimatePresence>
                  {isCurrent && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0.09 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="pointer-events-none absolute inset-0 flex select-none items-center justify-center text-[64px] font-black text-white"
                      aria-hidden
                    >
                      {step.step}
                    </motion.span>
                  )}
                </AnimatePresence>

                {/* Active border pulse */}
                <AnimatePresence>
                  {isCurrent && (
                    <motion.span
                      key="border-pulse"
                      className="pointer-events-none absolute inset-0 rounded-2xl border-2 border-white/40"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0.3, 0.8, 0.3] }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  )}
                </AnimatePresence>

                <div className="relative flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold uppercase tracking-[0.22em] opacity-80">
                    {step.eyebrow}
                  </span>

                  {/* Gamified badge */}
                  <div className="relative flex items-center justify-center">
                    {/* Pulse ring behind badge */}
                    <AnimatePresence>
                      {isCurrent && (
                        <motion.span
                          key="pulse-ring"
                          className="absolute -inset-2 rounded-full bg-white/25"
                          initial={{ scale: 0.5, opacity: 0 }}
                          animate={{ scale: [0.7, 1.9, 0.7], opacity: [0.5, 0, 0.5] }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
                        />
                      )}
                    </AnimatePresence>

                    <motion.span
                      layout
                      className={`relative z-10 inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                        isCurrent
                          ? 'bg-white/20 text-white'
                          : isCompleted
                            ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/40'
                            : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800'
                      }`}
                    >
                      <AnimatePresence mode="wait">
                        {isCompleted ? (
                          <motion.span
                            key="check"
                            className="flex items-center justify-center"
                            initial={{ scale: 0, rotate: -45 }}
                            animate={{ scale: 1, rotate: 0 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                          >
                            <CheckIcon className="h-4 w-4" />
                          </motion.span>
                        ) : (
                          <motion.span
                            key="num"
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.6, opacity: 0 }}
                            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                          >
                            {step.step}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </motion.span>
                  </div>
                </div>

                <p className="relative mt-2 text-sm font-semibold">{step.title}</p>
              </motion.button>
            </motion.div>
          );
        })}
      </div>

      {/* Overall XP progress bar */}
      <div className="mt-3 overflow-hidden rounded-full bg-neutral-200/80 dark:bg-neutral-700/80" style={{ height: 3 }}>
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-brand-500 via-brand-400 to-emerald-500"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: completionRatio }}
          style={{ transformOrigin: isRTL ? 'right center' : 'left center' }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </div>
  );
};

export default OnboardingStepper;
